import { useParams, Navigate } from 'react-router-dom';
import { useNewsArticles } from '../../../hooks/useNewsArticles';
import { localNews as mockArticles } from '../../../mocks/localNews';
import { useSEO, generateArticleSchema, generateBreadcrumbSchema } from '../../../utils/seo';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import NewsArticleHero from './components/NewsArticleHero';
import ArticleContent from './components/ArticleContent';
import RelatedStories from './components/RelatedStories';

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { articles } = useNewsArticles();
  
  // Try to find article in live data first, then fall back to mock data
  const allArticles = articles.length > 0 ? articles : mockArticles;
  const article = allArticles.find((a) => a.id === Number(id) || a.id.toString() === id);

  // Generate schemas
  const schemas = article ? {
    '@context': 'https://schema.org',
    '@graph': [
      generateArticleSchema(
        article.title,
        article.excerpt,
        `/news/${id}`,
        article.publishedAt || new Date().toISOString(),
        article.updatedAt || article.publishedAt || new Date().toISOString(),
        article.source || 'Roseville.org',
        article.image
      ),
      generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'News', url: '/news' },
        { name: article.title, url: `/news/${id}` }
      ])
    ]
  } : undefined;

  useSEO({
    title: article ? `${article.title} | Roseville Local News` : 'Article Not Found | Roseville.org',
    description: article?.excerpt || 'News article from Roseville, California',
    keywords: article ? `${article.category}, Roseville news, ${article.title.split(' ').slice(0, 4).join(', ')}, local news, California` : 'Roseville news',
    canonical: `/news/${id}`,
    ogType: 'article',
    ogImage: article?.image,
    ogImageAlt: article ? `${article.title} - Roseville News` : undefined,
    author: article?.source || 'Roseville.org',
    articlePublishedTime: article?.publishedAt || new Date().toISOString(),
    articleModifiedTime: article?.updatedAt || article?.publishedAt || new Date().toISOString(),
    schema: schemas,
  });

  if (!article) {
    return <Navigate to="/news" replace />;
  }

  // Get related stories from same category first, then fill with others
  const sameCategory = allArticles.filter((a) => a.category === article.category && a.id !== article.id);
  const others = allArticles.filter((a) => a.category !== article.category && a.id !== article.id);
  const relatedArticles = [...sameCategory, ...others].slice(0, 4);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <NewsArticleHero article={article} />
      <ArticleContent article={article} />
      <RelatedStories articles={relatedArticles} currentId={article.id} />
      <Footer />
    </div>
  );
}